import { Injectable } from '@angular/core';
import {
    DirectoryEntry,
    Entry,
    File,
    FileEntry,
    Flags,
    IWriteOptions,
    RemoveResult,
} from '@awesome-cordova-plugins/file/ngx';

/**
 * This service wraps all calls into the cordova file plugin into a native promise. This is necessary because of an
 * unfortunate interaction between the cordovaFile plugin and angularJS (which we still have to load for PACE Forms).
 * When cordovaFile detects angularJS, it will use $q as its Promise implementation. Unfortunately, $q is not aware of
 * zone.js.
 *
 * As consequence we will be running outside of the angular Zone after any promise out of cordovaFile resolves/rejects.
 * This breaks the angular change detection.
 *
 * As a workaround, we wrap all promise-returning functions of cordovaFile and wrap them into an additional native
 * promise. Now when the cordovaFile promises resolve, they will trigger the Zone-aware native promise and change
 * detection will work.
 *
 * This wrapper can be removed once we no longer have to load angularJS (after re-writing PACE Forms).
 */
@Injectable({
    providedIn: 'root',
})
export class FileWithZoneFixService extends File {
    public getFreeDiskSpace(): Promise<number> {
        return new Promise((resolve, reject) => {
            super
                .getFreeDiskSpace()
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public checkDir(path: string, dir: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            super
                .checkDir(path, dir)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public createDir(path: string, dirName: string, replace: boolean): Promise<DirectoryEntry> {
        return new Promise((resolve, reject) => {
            super
                .createDir(path, dirName, replace)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public removeDir(path: string, dirName: string): Promise<RemoveResult> {
        return new Promise((resolve, reject) => {
            super
                .removeDir(path, dirName)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public moveDir(
        path: string,
        dirName: string,
        newPath: string,
        newDirName: string,
    ): Promise<DirectoryEntry | Entry> {
        return new Promise((resolve, reject) => {
            super
                .moveDir(path, dirName, newPath, newDirName)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public copyDir(path: string, dirName: string, newPath: string, newDirName: string): Promise<Entry> {
        return new Promise((resolve, reject) => {
            super
                .copyDir(path, dirName, newPath, newDirName)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public listDir(path: string, dirName: string): Promise<Entry[]> {
        return new Promise((resolve, reject) => {
            super
                .listDir(path, dirName)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public removeRecursively(path: string, dirName: string): Promise<RemoveResult> {
        return new Promise((resolve, reject) => {
            super
                .removeRecursively(path, dirName)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public checkFile(path: string, file: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            super
                .checkFile(path, file)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public createFile(path: string, fileName: string, replace: boolean): Promise<FileEntry> {
        return new Promise((resolve, reject) => {
            super
                .createFile(path, fileName, replace)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public removeFile(path: string, fileName: string): Promise<RemoveResult> {
        return new Promise((resolve, reject) => {
            super
                .removeFile(path, fileName)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public writeFile(
        path: string,
        fileName: string,
        text: string | Blob | ArrayBuffer,
        options?: IWriteOptions,
    ): Promise<unknown> {
        return new Promise((resolve, reject) => {
            super
                .writeFile(path, fileName, text, options)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public writeExistingFile(path: string, fileName: string, text: string | Blob): Promise<void> {
        return new Promise((resolve, reject) => {
            super
                .writeExistingFile(path, fileName, text)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public readAsText(path: string, file: string): Promise<string> {
        return new Promise((resolve, reject) => {
            super
                .readAsText(path, file)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public readAsDataURL(path: string, file: string): Promise<string> {
        return new Promise((resolve, reject) => {
            super
                .readAsDataURL(path, file)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public readAsBinaryString(path: string, file: string): Promise<string> {
        return new Promise((resolve, reject) => {
            super
                .readAsBinaryString(path, file)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public readAsArrayBuffer(path: string, file: string): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            super
                .readAsArrayBuffer(path, file)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public moveFile(path: string, fileName: string, newPath: string, newFileName: string): Promise<Entry> {
        return new Promise((resolve, reject) => {
            super
                .moveFile(path, fileName, newPath, newFileName)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public copyFile(path: string, fileName: string, newPath: string, newFileName: string): Promise<Entry> {
        return new Promise((resolve, reject) => {
            super
                .copyFile(path, fileName, newPath, newFileName)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public resolveLocalFilesystemUrl(fileUrl: string): Promise<Entry> {
        return new Promise((resolve, reject) => {
            super
                .resolveLocalFilesystemUrl(fileUrl)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public resolveDirectoryUrl(directoryUrl: string): Promise<DirectoryEntry> {
        return new Promise((resolve, reject) => {
            super
                .resolveDirectoryUrl(directoryUrl)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public getDirectory(directoryEntry: DirectoryEntry, directoryName: string, flags: Flags): Promise<DirectoryEntry> {
        return new Promise((resolve, reject) => {
            super
                .getDirectory(directoryEntry, directoryName, flags)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
    public getFile(directoryEntry: DirectoryEntry, fileName: string, flags: Flags): Promise<FileEntry> {
        return new Promise((resolve, reject) => {
            super
                .getFile(directoryEntry, fileName, flags)
                .then((result) => resolve(result))
                .catch((error) => reject(error));
        });
    }
}
